import { inject, Injectable, signal } from '@angular/core';

import {
  Toast,
  ToastAction,
  ToastPosition,
  ToastStatus,
} from '../../core/models/toast.model';
import { I18nService } from '../../core/services/i18n.service';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  toasts = signal<Toast[]>([]);
  private i18nService = inject(I18nService);

  show(
    message: string,
    status: ToastStatus = 'info',
    duration: number = 5000,
    position: ToastPosition = 'top-center',
    action?: ToastAction       
  ): number {
    const newToast: Toast = {
      message,
      status,
      duration,
      position,
      id: Date.now(),
      action,
    };

    this.toasts.update(toasts => [...toasts, newToast]);

    if (duration > 0) {
      setTimeout(() => this.remove(newToast.id), duration);
    }
    
    return newToast.id;
  }

  remove(id: number) {
    this.toasts.update(toasts => toasts.filter(toast => toast.id !== id));
  }

  showSuccess(
    message: string,
    duration: number = 5000,
    position: ToastPosition = 'top-center',
    onUndo?: () => void, 
    undoLabel?: string
  ): number {
    const finalLabel = undoLabel || this.i18nService.instant('shared.toast.undo');
    const action = onUndo ? { label: finalLabel, onClick: onUndo } : undefined;
    return this.show(message, 'success', duration, position, action);
  }

  showError(
    message: string,
    duration: number = 5000,
    position: ToastPosition = 'top-center'
  ) {
    this.show(message, 'error', duration, position);
  }

  showWarning(
    message: string,
    duration: number = 5000,
    position: ToastPosition = 'top-center'
  ) {
    this.show(message, 'warning', duration, position);
  }
}
